/**
 * Times shown to the user are Central time, whatever the browser's zone and whatever zone a
 * lender prints on its own sheet (First Colony MST, PennyMac / Sun West / AmWest PT, The Loan
 * Store ET): every instant is formatted in America/Chicago and labelled "CT" (the user's ask,
 * 2026-09-23 — "in CST at all times").
 */
const ZONE = 'America/Chicago';

/** "Sep 23, 1:07 PM CT" (with the year when it isn't this year); "—" when there is no instant. */
export function fmtCT(iso: string | Date | number | null | undefined, opts: { year?: boolean; dateOnly?: boolean } = {}): string {
  if (iso == null || iso === '') return '—';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const year = opts.year ?? d.getFullYear() !== new Date().getFullYear();
  const text = d.toLocaleString('en-US', {
    timeZone: ZONE,
    month: 'short',
    day: 'numeric',
    ...(year ? { year: 'numeric' } : {}),
    ...(opts.dateOnly ? {} : { hour: 'numeric', minute: '2-digit' }),
  });
  return opts.dateOnly ? text : `${text} CT`;
}

/** "1:07 PM CT" — the time alone, for a line that already names the day. */
export function fmtTimeCT(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleTimeString('en-US', { timeZone: ZONE, hour: 'numeric', minute: '2-digit' })} CT`;
}

const ZONE_OF: Record<string, string> = { ET: 'America/New_York', EDT: 'America/New_York', EST: 'America/New_York', CT: ZONE, CDT: ZONE, CST: ZONE, MT: 'America/Denver', MDT: 'America/Denver', MST: 'America/Denver', PT: 'America/Los_Angeles', PDT: 'America/Los_Angeles', PST: 'America/Los_Angeles' };

/** The instant of a wall-clock time in a zone (the zone's offset read at that moment). */
function zonedInstant(y: number, mo: number, d: number, h: number, mi: number, zone: string): number {
  const naive = Date.UTC(y, mo - 1, d, h, mi);
  const tz = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortOffset' }).formatToParts(new Date(naive)).find((p) => p.type === 'timeZoneName')?.value ?? 'GMT';
  const m = tz.match(/GMT([+-])(\d+)(?::(\d+))?/);
  const offsetMin = m ? (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] ?? 0)) : 0;
  return naive - offsetMin * 60_000;
}

/**
 * The instant a sheet's cover prints, from its own text: "2026-09-23 12:38 ET", "2026-09-23 07:20 PT",
 * "9/23/26 7:23 AM MST" (First Colony prints MST year-round for Mountain time). Null when the cover
 * carries no zone (PennyMac's "2026-09-23 09:24:00") or no time at all (Spring EQ's "2026-09-18").
 */
function printedInstant(sheetDateTime: string): number | null {
  const iso = sheetDateTime.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}):(\d{2})(?::\d{2})?\s*([A-Z]{2,3})\b/);
  if (iso && ZONE_OF[iso[6]!]) return zonedInstant(Number(iso[1]), Number(iso[2]), Number(iso[3]), Number(iso[4]), Number(iso[5]), ZONE_OF[iso[6]!]!);
  const us = sheetDateTime.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)\s*([A-Z]{2,3})\b/i);
  if (us && ZONE_OF[us[7]!.toUpperCase()]) {
    const year = us[3]!.length === 2 ? 2000 + Number(us[3]) : Number(us[3]);
    const h = (Number(us[4]) % 12) + (us[6]!.toUpperCase() === 'PM' ? 12 : 0);
    return zonedInstant(year, Number(us[1]), Number(us[2]), h, Number(us[5]), ZONE_OF[us[7]!.toUpperCase()]!);
  }
  return null;
}

/**
 * A rate sheet's own publish time in Central time: the time its cover prints (converted from the
 * lender's zone), else the parsed instant Mana stored, else the cover text as printed. A cover
 * that prints only a day (Spring EQ) shows that day; The Loan Store's version within the day
 * (2026-09-23/v05) is kept.
 */
export function fmtSheetStamp(sheet: { sheetAt?: string | null; sheetDateTime: string | null; versionKey: string }): string {
  const printed = sheet.sheetDateTime?.trim() ?? '';
  const version = sheet.versionKey.match(/\/(v\d+)$/)?.[1] ?? printed.match(/·\s*(v\d+)$/)?.[1] ?? null;
  const withVersion = (s: string) => (version ? `${s} · ${version}` : s);
  const day = printed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (day) return withVersion(fmtCT(Date.UTC(Number(day[1]), Number(day[2]) - 1, Number(day[3]), 12), { dateOnly: true, year: true }));
  const instant = printed ? printedInstant(printed) : null;
  if (instant != null) return withVersion(fmtCT(instant));
  if (sheet.sheetAt) return withVersion(fmtCT(sheet.sheetAt));
  return printed || sheet.versionKey;
}
